/**
 * GoalCard Component
 * Display goal with completion status and interactive completion button
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Goal } from '../models/Goal';

interface GoalCardProps {
  goal: Goal;
  isCompleted: boolean;
  onToggleCompletion: () => void;
  showType?: boolean;
  disabled?: boolean;
  themeColor?: string;
}

export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  isCompleted,
  onToggleCompletion,
  showType = false,
  disabled = false,
  themeColor = '#007AFF',
}) => {
  return (
    <View style={[styles.card, goal.type === 'main' && [styles.mainCard, { borderColor: themeColor }]]}>
      {/* Goal Info */}
      <View style={styles.goalInfo}>
        <Text style={[styles.goalName, goal.type === 'main' && [styles.mainGoalName, { color: themeColor }]]}>
          {goal.name}
        </Text>
        {showType && (
          <Text style={styles.goalType}>
            {goal.type === 'main' ? 'Main Goal' : 'Side Goal'}
          </Text>
        )}
      </View>

      {/* Completion Button */}
      <TouchableOpacity
        style={[
          styles.completionButton,
          isCompleted && styles.completionButtonCompleted,
          disabled && styles.completionButtonDisabled,
        ]}
        onPress={onToggleCompletion}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <View style={[styles.checkmark, isCompleted && styles.checkmarkVisible]}>
          <Text style={styles.checkmarkText}>✓</Text>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginVertical: 6,
    marginHorizontal: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  mainCard: {
    backgroundColor: '#F0F8FF',
    borderWidth: 2,
    borderColor: '#007AFF',
  },
  goalInfo: {
    flex: 1,
    marginRight: 12,
  },
  goalName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    lineHeight: 22,
  },
  mainGoalName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#007AFF',
  },
  goalType: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  completionButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: '#ccc',
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  completionButtonCompleted: {
    borderColor: '#34C759',
    backgroundColor: '#34C759',
  },
  completionButtonDisabled: {
    opacity: 0.5,
  },
  checkmark: {
    opacity: 0,
  },
  checkmarkVisible: {
    opacity: 1,
  },
  checkmarkText: {
    fontSize: 28,
    color: '#fff',
    fontWeight: 'bold',
    lineHeight: 32,
    marginTop: Platform.OS === 'ios' ? -2 : 0,
  },
});
